import {
  closestCenter,
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { BoardPageBoard } from '@molt/shared';
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { dragAnnouncements, dragInstructions, useMoltSensors } from '@/lib/dnd';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { neighboursForMove, type DragHandle } from '@/lib/list-drag';
import { BoardTile } from './BoardTile';

export interface BoardShortcutSectionProps {
  title: string;
  listLabel: string;
  boards: readonly BoardPageBoard[];
  starredBoardIds: ReadonlySet<string>;
  reorderable?: boolean | undefined;
  onToggleStar?: ((board: BoardPageBoard, nextStarred: boolean) => void) | undefined;
  onReorderStarredBoard?:
    | ((
        boardId: string,
        neighbours: { previousBoardId: string | null; nextBoardId: string | null },
      ) => void)
    | undefined;
}

export function BoardShortcutSection({
  title,
  listLabel,
  boards,
  starredBoardIds,
  reorderable = false,
  onToggleStar,
  onReorderStarredBoard,
}: BoardShortcutSectionProps): ReactElement | null {
  /* ⚠ FE-80.1 — this file was NOT on the board-face slice's list. It was
   * pulled in by making `t` a required first parameter of `lib/dnd.ts`'s two
   * exports, which is the whole argument for a required parameter over a
   * defaulted one: the second author of an unrouted shared leaf is found by
   * `tsc` rather than by a later scan. */
  const { t } = useInterfaceLanguage();
  const sensors = useMoltSensors();
  const [activeId, setActiveId] = useState<string | null>(null);
  const ids = useMemo(() => boards.map((board) => board.id), [boards]);
  const activeBoard = boards.find((board) => board.id === activeId);
  const canReorder = reorderable && onReorderStarredBoard !== undefined;

  if (boards.length === 0) return null;

  function describe(id: string): string {
    return boards.find((board) => board.id === id)?.title ?? t('boardShortcuts.dragItem');
  }

  function handleDragStart(event: DragStartEvent): void {
    if (!canReorder) return;
    setActiveId(String(event.active.id));
  }

  function handleDragEnd(event: DragEndEvent): void {
    if (!canReorder) return;
    const boardId = String(event.active.id);
    const overId = event.over === null ? null : String(event.over.id);
    setActiveId(null);
    if (overId === null) return;
    const neighbours = neighboursForMove(ids, boardId, overId);
    if (neighbours === null) return;
    onReorderStarredBoard(boardId, {
      previousBoardId: neighbours.previousId,
      nextBoardId: neighbours.nextId,
    });
  }

  const grid = (
    <ol
      aria-label={listLabel}
      className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4"
    >
      {boards.map((board) =>
        canReorder ? (
          <SortableShortcutTile
            key={board.id}
            board={board}
            starred={starredBoardIds.has(board.id)}
            onToggleStar={onToggleStar}
          />
        ) : (
          <li key={board.id}>
            <BoardTile
              board={board}
              starred={starredBoardIds.has(board.id)}
              onToggleStar={onToggleStar}
            />
          </li>
        ),
      )}
    </ol>
  );

  const headingId = `${listLabel.replaceAll(' ', '-')}-heading`;

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-3">
      <h2 id={headingId} className="font-display text-lg font-semibold text-ink">
        {title}
      </h2>
      {canReorder ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          accessibility={{
            screenReaderInstructions: dragInstructions(t),
            announcements: dragAnnouncements(t, describe),
          }}
          onDragStart={handleDragStart}
          onDragCancel={() => setActiveId(null)}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={ids} strategy={rectSortingStrategy}>
            {grid}
          </SortableContext>
          <DragOverlay>
            {activeBoard === undefined ? null : (
              <div className="w-[min(24rem,80vw)]">
                <BoardTile
                  board={activeBoard}
                  starred={starredBoardIds.has(activeBoard.id)}
                />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      ) : (
        grid
      )}
    </section>
  );
}

interface SortableShortcutTileProps {
  board: BoardPageBoard;
  starred: boolean;
  onToggleStar?: ((board: BoardPageBoard, nextStarred: boolean) => void) | undefined;
}

function SortableShortcutTile({
  board,
  starred,
  onToggleStar,
}: SortableShortcutTileProps): ReactElement {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: board.id,
    attributes: { role: 'link' },
  });

  const dragHandle: DragHandle = {
    setActivatorNodeRef,
    attributes,
    listeners,
    isDragging,
  };

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={isDragging ? 'z-10 opacity-90' : undefined}
    >
      <BoardTile
        board={board}
        starred={starred}
        onToggleStar={onToggleStar}
        dragHandle={dragHandle}
      />
    </li>
  );
}
